import Cookies from 'cookies-js'
import { isUndefined, omitBy } from 'lodash-es'

import TokenService from '../authService/TokenService'
import isOnePlatformEnabled from '../isOnePlatformEnabled'

export enum HTTPMethod {
  CONNECT = 'CONNECT',
  DELETE = 'DELETE',
  GET = 'GET',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
  TRACE = 'TRACE',
}

export type RequestOptions = {
  url: string
  method: HTTPMethod
  data?: Record<string, unknown>
  files?: Record<string, File | undefined>
}

export type ApiResponse<Data = unknown> = {
  status: 'ok' | 'error'
  p: number
  total_count: number
  size: number
  offset: number
  data: Data
}

export type ApiError = {
  code: string
  message: string
  status: 'error'
}

export type ErrorWithApiCause = Error & { cause: ApiError }

export const isErrorWithApiCause = (
  error: unknown
): error is ErrorWithApiCause => {
  return isError(error) && isApiError(error.cause)
}

export const isError = (error: unknown): error is Error => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  )
}

export const isApiError = (apiError: unknown): apiError is ApiError => {
  return (
    typeof apiError === 'object' &&
    apiError !== null &&
    'message' in apiError &&
    'code' in apiError &&
    'status' in apiError &&
    typeof apiError.message === 'string'
  )
}

const performRequest = async (
  baseDomain: string | undefined,
  bearerToken: string | undefined,
  options: RequestOptions
) => {
  const fetchOptions = {
    method: options.method,
    credentials: 'include',
    headers: new Headers(),
  } as const

  if (bearerToken) {
    fetchOptions.headers.set('Authorization', `Bearer ${bearerToken}`)
  }

  const cookie = Cookies.get('_tt_dev_env')
  if (cookie) {
    fetchOptions.headers.set('TwentyThree-Development-Environment', cookie)
  }

  const cleanData = omitBy(options.data, isUndefined)
  const url = new URL(options.url, baseDomain ?? window.location.origin)

  let response
  if (['HEAD', 'GET'].includes(options.method)) {
    // @ts-expect-error — will be type cast
    response = await fetch(`${url.href}?${new URLSearchParams(cleanData)}`, {
      ...fetchOptions,
    })
  } else {
    const formData = new FormData()

    Object.entries(cleanData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(key, v))
      } else {
        // @ts-expect-error — all values will be cast correctly
        formData.append(key, value)
      }
    })
    if (options.files) {
      Object.entries(options.files).forEach(([key, file]) => {
        if (file) {
          formData.append(key, file)
        }
      })
    }
    response = await fetch(url.href, {
      body: formData,
      ...fetchOptions,
    })
  }

  if (response.status >= 400 && response.status < 600) {
    const apiError = (await response.json()) as ApiError
    throw new Error(apiError.message, { cause: apiError })
  }

  try {
    return await response.json()
  } catch (e) {
    throw new Error('Failed to parse response')
  }
}

const productRequest = async <ResponseData extends ApiResponse>(
  productKey: 'personal' | 'tube',
  options: RequestOptions
): Promise<ResponseData> => {
  const sites = TokenService.getInstance().getTokens()
  const productWorkspace = sites.find((site) => site.product_key === productKey)

  if (!productWorkspace) {
    throw new Error(`No ${productKey} workspace available to use`)
  }

  return performRequest(
    productWorkspace.api_base_url,
    productWorkspace.bearer_token,
    options
  )
}

const workspaceRequestByKey = async <ResponseData extends ApiResponse>(
  workspaceKey: string,
  options: RequestOptions
): Promise<ResponseData> => {
  const sites = TokenService.getInstance().getTokens()
  const workspace = sites.find((site) => site.site_key === workspaceKey)

  if (!workspace) {
    throw new Error(
      `No workspace that matches the key ${workspaceKey} is available to use`
    )
  }

  return performRequest(workspace.api_base_url, workspace.bearer_token, options)
}

const workspaceRequestById = async <ResponseData extends ApiResponse>(
  workspaceId: number,
  options: RequestOptions
): Promise<ResponseData> => {
  const sites = TokenService.getInstance().getTokens()
  const workspace = sites.find((site) => site.site_id === workspaceId)

  if (!workspace) {
    throw new Error(
      `No workspace that matches the id ${workspaceId} is available to use`
    )
  }

  return performRequest(workspace.api_base_url, workspace.bearer_token, options)
}

const currentWorkspaceRequest = async <ResponseData extends ApiResponse>(
  options: RequestOptions
): Promise<ResponseData> => {
  const currentWorkspace = isOnePlatformEnabled
    ? TokenService.getInstance().getCurrentSiteToken()
    : undefined

  return performRequest(
    currentWorkspace?.api_base_url,
    currentWorkspace?.bearer_token,
    options
  )
}

const unauthenticatedRequest = async <ResponseData extends ApiResponse>(
  baseDomain: string,
  options: RequestOptions
): Promise<ResponseData> => {
  return performRequest(baseDomain, undefined, options)
}

export {
  productRequest,
  workspaceRequestByKey,
  workspaceRequestById,
  currentWorkspaceRequest,
  unauthenticatedRequest,
}
